import { extractStateFromHost, queueSyncCartHost, seedCartHostState } from './cart-live-announce-shared.js';

const ZNT_CART_LOG = '[znt/cart-state]';

function zntCartLog(...args) {
  console.log(ZNT_CART_LOG, ...args);
}

function zntCartWarn(...args) {
  console.warn(ZNT_CART_LOG, ...args);
}

/** Snapshot for debugging */
function logDomSnapshot(reason) {
  const liveNodes = [...document.querySelectorAll('[data-znt-cart-live-region]')];
  zntCartLog(`DOM snapshot (${reason})`, {
    path: window.location.pathname,
    onCartTemplate: window.location.pathname.includes('/cart'),
    cartPageRoot: !!document.querySelector('.cart-page'),
    mainCartHostSectionId: getMainCartItemsHost()?.dataset?.sectionId ?? null,
    liveRegionNodes: liveNodes.map((el) => ({
      id: el.id || '(no id)',
      inCartPage: !!el.closest('.cart-page'),
      inOpenDialog: (() => {
        const d = el.closest('dialog');
        return !!(d && d.open);
      })(),
      inDialog: !!el.closest('dialog'),
      parentCartHost: el.closest('cart-items-component')?.dataset?.sectionId ?? null,
    })),
    cartItemsHosts: [...document.querySelectorAll('cart-items-component')].map((el) => ({
      sectionId: el.dataset.sectionId,
      inCartPage: !!el.closest('.cart-page'),
      inDialog: !!el.closest('dialog'),
    })),
  });
}

zntCartLog('script loaded', {
  href: window.location.href,
  readyState: document.readyState,
  note: 'Cart page: MutationObserver queues detailed announce via cart-live-announce-shared.js (same as global cart-live-region.js).',
});

function getCartPageRoot() {
  return document.querySelector('.cart-page');
}

function getMainCartItemsHost() {
  return getCartPageRoot()?.closest('cart-items-component') ?? null;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (window.location.pathname.includes('/cart')) {
      logCartState();
    } else {
      zntCartLog('skip init — not /cart path (logger only runs on cart template)');
    }
  });
} else if (window.location.pathname.includes('/cart')) {
  logCartState();
} else {
  zntCartLog('skip init — not /cart path (logger only runs on cart template)');
}

function logCartState() {
  zntCartLog('init scheduled (1s delay for DOM)');

  setTimeout(() => {
    zntCartLog('initial extract + setupChangeDetection');
    logDomSnapshot('after 1s delay');
    const host = getMainCartItemsHost();
    const state = extractStateFromHost(host);
    zntCartLog('initial state summary', {
      products: state.products.length,
      totalItems: state.totalItems,
      estimatedTotal: state.estimatedTotal,
    });
    if (host) seedCartHostState(host);
    setupChangeDetection();
  }, 1000);
}

function setupChangeDetection() {
  zntCartLog('setupChangeDetection');

  function checkForChanges() {
    const mainHost = getMainCartItemsHost();
    if (!mainHost) {
      zntCartWarn('checkForChanges: no main cart host');
      return;
    }

    const next = extractStateFromHost(mainHost);
    zntCartLog('checkForChanges → queueSyncCartHost', {
      sectionId: mainHost.dataset.sectionId,
      rows: next.products.length,
      totalItems: next.totalItems,
    });

    queueSyncCartHost(mainHost);
  }

  const mainCartHost = getMainCartItemsHost();
  if (mainCartHost) {
    zntCartLog('MutationObserver attached', {
      host: 'cart-items-component',
      sectionId: mainCartHost.dataset.sectionId,
    });

    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;

      for (const mutation of mutations) {
        if (
          mutation.type === 'childList' &&
          (mutation.target.classList?.contains('cart-items__table') ||
            mutation.target.querySelector?.('.cart-items__table'))
        ) {
          shouldCheck = true;
          break;
        }
        if (mutation.type === 'characterData' || (mutation.type === 'childList' && mutation.addedNodes.length > 0)) {
          shouldCheck = true;
          break;
        }
      }

      if (shouldCheck) {
        zntCartLog('MutationObserver → mutations', {
          count: mutations.length,
          types: [...new Set(mutations.map((m) => m.type))],
        });
        setTimeout(() => checkForChanges(), 100);
      }
    });

    observer.observe(mainCartHost, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    zntCartLog('change detection active (observer → shared queue; cart:update handled globally)');
  } else {
    zntCartWarn('no .cart-page cart-items host — no subtree observer');
    logDomSnapshot('missing mainCartHost');
  }
}
